import { renderBrandedQrDataUrl } from '@/lib/brandedQr'
import { posterQrStyle } from '@/components/admin/marketing/posterThemes'
import { sizedImageUrl, wrapLines, type KitFormat } from '@/lib/kitRenderUtils'

export { kitFileName, sizedImageUrl, wrapLines } from '@/lib/kitRenderUtils'
export type { KitFormat } from '@/lib/kitRenderUtils'

// Marketing kit visuals (F-MKT-N1) — the post, story, print poster and
// review quote cards a kit hands the owner, painted with Canvas 2D from the
// restaurant's own dish photos, logo and the kit's words.
//
// Deliberately not an editor: the owner picks a format, a look and which
// dish photo leads; everything else comes from the kit. Same technique as
// posterExport.ts — every image is fetched to a blob first, so the canvas is
// never tainted and toBlob() always works.

export type KitLook = 'warm' | 'night' | 'fresh' | 'paper'

export const KIT_FORMATS: { id: KitFormat; label: string; size: string }[] = [
  { id: 'post', label: 'Feed post', size: '1080 × 1350' },
  { id: 'story', label: 'Story / Status', size: '1080 × 1920' },
  { id: 'poster', label: 'Print poster', size: 'A4' },
  { id: 'quote', label: 'Quote card', size: '1080 × 1350' },
]

export const KIT_DIMS: Record<KitFormat, { w: number; h: number }> = {
  post: { w: 1080, h: 1350 },
  story: { w: 1080, h: 1920 },
  // A4 at 150 dpi — sharp at arm's length, small enough to share.
  poster: { w: 1240, h: 1754 },
  quote: { w: 1080, h: 1350 },
}

interface LookPalette {
  label: string
  dark: boolean
  bg: string
  ink: string
  muted: string
  accent: string
  accentInk: string
}

export const KIT_LOOKS: Record<KitLook, LookPalette> = {
  warm: { label: 'Saffron', dark: true, bg: '#2a1a12', ink: '#fff4e6', muted: '#e9d6c0', accent: '#f2a541', accentInk: '#2a1a12' },
  night: { label: 'Night', dark: true, bg: '#0f1115', ink: '#ffffff', muted: '#c9ced8', accent: '#ff6a3d', accentInk: '#ffffff' },
  fresh: { label: 'Fresh', dark: false, bg: '#eef4e8', ink: '#14301f', muted: '#4b6354', accent: '#2f8f4e', accentInk: '#ffffff' },
  paper: { label: 'Paper', dark: false, bg: '#fbf7f0', ink: '#1c1a17', muted: '#6b625a', accent: '#c2410c', accentInk: '#ffffff' },
}

const SERIF = '"Instrument Serif", "Iowan Old Style", Georgia, serif'
const SANS = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif'

export interface KitRenderInput {
  format: KitFormat
  look: KitLook
  restaurant: string
  logoDataUrl: string | null
  photoDataUrl: string | null
  headline: string
  subhead: string
  offerLine: string
  storyText: string
  when: string
  dishName?: string
  quote?: { text: string; author: string }
  /** Poster only: where the QR points (the menu). Empty = no QR. */
  qrUrl?: string
  qrCaption?: string
}

// ── Image loading ─────────────────────────────────────────────────────

const imageCache = new Map<string, Promise<string | null>>()

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('read failed'))
    r.readAsDataURL(blob)
  })
}


/** Fetch an image to a data: URL; null when it can't be read cleanly. */
export function loadImageDataUrl(url: string | null | undefined): Promise<string | null> {
  const src = (url || '').trim()
  if (!src) return Promise.resolve(null)
  if (src.startsWith('data:')) return Promise.resolve(src)
  let abs: string
  try {
    abs = new URL(sizedImageUrl(src), window.location.origin).toString()
  } catch {
    return Promise.resolve(null)
  }
  const hit = imageCache.get(abs)
  if (hit) return hit
  const task = (async () => {
    try {
      const res = await fetch(abs, { mode: 'cors' })
      if (!res.ok) return null
      return await blobToDataUrl(await res.blob())
    } catch {
      return null
    }
  })()
  imageCache.set(abs, task)
  return task
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image failed to load'))
    img.src = src
  })
}

// ── Drawing helpers ───────────────────────────────────────────────────

/** Draw an image to cover a box, centred (CSS object-fit: cover). */
function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
  const s = Math.max(w / img.naturalWidth, h / img.naturalHeight)
  const sw = w / s
  const sh = h / s
  const sx = (img.naturalWidth - sw) / 2
  const sy = (img.naturalHeight - sh) / 2
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h)
}


/**
 * Pick the largest font size (stepping down) at which the text fits in
 * maxLines. Returns the size and the wrapped lines.
 */
function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  font: (px: number) => string,
  maxWidth: number,
  maxLines: number,
  startPx: number,
  minPx: number,
): { px: number; lines: string[] } {
  for (let px = startPx; px >= minPx; px -= 4) {
    ctx.font = font(px)
    const lines = wrapLines(text, maxWidth, (s) => ctx.measureText(s).width)
    if (lines.length <= maxLines) return { px, lines }
  }
  ctx.font = font(minPx)
  const lines = wrapLines(text, maxWidth, (s) => ctx.measureText(s).width)
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines)
    kept[maxLines - 1] = kept[maxLines - 1].replace(/\s*\S*$/, '') + '…'
    return { px: minPx, lines: kept }
  }
  return { px: minPx, lines }
}

function drawLines(ctx: CanvasRenderingContext2D, lines: string[], x: number, y: number, lineHeight: number) {
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight))
  return y + lines.length * lineHeight
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** An accent pill with the offer line; returns its bottom edge. */
function drawPill(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, px: number, look: LookPalette) {
  ctx.font = `600 ${px}px ${SANS}`
  const fitted = fitText(ctx, text, (p) => `600 ${p}px ${SANS}`, maxW - px * 1.6, 1, px, Math.max(18, px - 16))
  ctx.font = `600 ${fitted.px}px ${SANS}`
  const w = Math.min(maxW, ctx.measureText(fitted.lines[0]).width + fitted.px * 1.6)
  const h = fitted.px * 2.1
  ctx.fillStyle = look.accent
  roundRect(ctx, x, y, w, h, h / 2)
  ctx.fill()
  ctx.fillStyle = look.accentInk
  ctx.textBaseline = 'middle'
  ctx.fillText(fitted.lines[0], x + fitted.px * 0.8, y + h / 2 + 1)
  ctx.textBaseline = 'alphabetic'
  return y + h
}

/** Logo (or a monogram) plus the restaurant name, top-left. */
async function drawBrand(ctx: CanvasRenderingContext2D, input: KitRenderInput, x: number, y: number, size: number, color: string) {
  let drewLogo = false
  if (input.logoDataUrl) {
    try {
      const logo = await loadImage(input.logoDataUrl)
      ctx.save()
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
      ctx.clip()
      const s = Math.min((size * 0.8) / logo.naturalWidth, (size * 0.8) / logo.naturalHeight)
      ctx.drawImage(logo, x + (size - logo.naturalWidth * s) / 2, y + (size - logo.naturalHeight * s) / 2, logo.naturalWidth * s, logo.naturalHeight * s)
      ctx.restore()
      drewLogo = true
    } catch {
      drewLogo = false
    }
  }
  const textX = drewLogo ? x + size + size * 0.3 : x
  ctx.fillStyle = color
  ctx.font = `600 ${Math.round(size * 0.42)}px ${SANS}`
  ctx.textBaseline = 'middle'
  ctx.fillText(input.restaurant.toUpperCase(), textX, y + size / 2 + 1)
  ctx.textBaseline = 'alphabetic'
}

// ── Layouts ───────────────────────────────────────────────────────────

async function paintPhotoLayout(ctx: CanvasRenderingContext2D, input: KitRenderInput, W: number, H: number, look: LookPalette) {
  const pad = Math.round(W * 0.07)
  const photo = input.photoDataUrl ? await loadImage(input.photoDataUrl).catch(() => null) : null
  const isStory = input.format === 'story'

  if (look.dark) {
    // Full-bleed photo, darkened towards the bottom where the words sit.
    ctx.fillStyle = look.bg
    ctx.fillRect(0, 0, W, H)
    if (photo) {
      drawCover(ctx, photo, 0, 0, W, H)
      const g = ctx.createLinearGradient(0, H * 0.25, 0, H)
      g.addColorStop(0, 'rgba(0,0,0,0)')
      g.addColorStop(0.55, hexA(look.bg, 0.78))
      g.addColorStop(1, hexA(look.bg, 0.96))
      ctx.fillStyle = g
      ctx.fillRect(0, 0, W, H)
      const top = ctx.createLinearGradient(0, 0, 0, H * 0.18)
      top.addColorStop(0, 'rgba(0,0,0,0.45)')
      top.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = top
      ctx.fillRect(0, 0, W, H * 0.18)
    } else {
      paintTexture(ctx, W, H, look)
    }
  } else {
    // Photo panel on top, solid colour below.
    ctx.fillStyle = look.bg
    ctx.fillRect(0, 0, W, H)
    const panelH = Math.round(H * (isStory ? 0.56 : 0.54))
    if (photo) {
      drawCover(ctx, photo, 0, 0, W, panelH)
      const top = ctx.createLinearGradient(0, 0, 0, H * 0.14)
      top.addColorStop(0, 'rgba(0,0,0,0.35)')
      top.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = top
      ctx.fillRect(0, 0, W, H * 0.14)
    } else {
      ctx.fillStyle = look.accent
      ctx.fillRect(0, 0, W, panelH)
      paintTexture(ctx, W, panelH, { ...look, bg: look.accent })
    }
  }

  const brandColor = photo || look.dark ? '#ffffff' : look.accentInk
  await drawBrand(ctx, input, pad, pad, Math.round(W * 0.075), brandColor)

  // Words, bottom-up.
  const maxW = W - pad * 2
  const headline = input.format === 'story' && input.storyText ? input.storyText : input.headline
  // The story's big words are often the offer itself; don't say it twice.
  const offerCore = input.offerLine.split(' · ')[0].trim().toLowerCase()
  const repeats = Boolean(offerCore) && headline.toLowerCase().includes(offerCore)
  const pillText = repeats ? input.offerLine.split(' · ').slice(1).join(' · ').trim() : input.offerLine
  const showPill = Boolean(pillText)
  const pillPx = isStory ? 40 : 36
  const whenPx = 28
  const pillH = pillPx * 2.1
  // On light looks the words must stay below the photo panel; dark looks
  // may run up the photo, but never past the brand line.
  // A story keeps clear of the reply bar at the bottom.
  const bottom = H - pad - (isStory ? H * 0.08 : 0)
  const ceiling = look.dark ? H * 0.3 : Math.round(H * (isStory ? 0.56 : 0.54)) + pad * 0.6
  let headStart = isStory ? 150 : 124
  let subStart = isStory ? 42 : 38
  let head = fitText(ctx, headline, (p) => `${p}px ${SERIF}`, maxW, 3, headStart, 56)
  let sub = { px: 0, lines: [] as string[] }
  let blockH = 0
  // Measure the block with exactly the steps that draw it; shrink until it
  // fits between the ceiling and the bottom margin.
  for (let i = 0; i < 12; i++) {
    sub = input.subhead
      ? fitText(ctx, input.subhead, (p) => `400 ${p}px ${SANS}`, maxW, 3, subStart, 24)
      : { px: 0, lines: [] as string[] }
    blockH = head.px * 0.8 + head.lines.length * head.px * 0.98
    if (sub.lines.length) blockH += 18 + sub.px * 0.4 + sub.lines.length * sub.px * 1.3
    if (showPill) blockH += 10 + pillH
    if (input.when) blockH += whenPx * 1.7
    if (bottom - blockH >= ceiling || (head.px <= 56 && sub.px <= 24)) break
    headStart = Math.max(56, head.px - 8)
    subStart = Math.max(24, sub.px - 2)
    head = fitText(ctx, headline, (p) => `${p}px ${SERIF}`, maxW, 3, headStart, 56)
  }
  const headStep = head.px * 0.98
  const subStep = sub.px * 1.3
  let y = bottom - blockH + head.px * 0.8

  ctx.fillStyle = look.ink
  ctx.font = `${head.px}px ${SERIF}`
  y = drawLines(ctx, head.lines, pad, y, headStep)
  if (sub.lines.length) {
    y += 18
    ctx.fillStyle = look.muted
    ctx.font = `400 ${sub.px}px ${SANS}`
    y = drawLines(ctx, sub.lines, pad, y + sub.px * 0.4, subStep)
  }
  if (showPill) {
    y = drawPill(ctx, pillText, pad, y + 10, maxW, pillPx, look)
  }
  if (input.when) {
    ctx.fillStyle = look.muted
    ctx.font = `500 ${whenPx}px ${SANS}`
    ctx.fillText(input.when, pad, y + whenPx * 1.7)
  }
}

function paintTexture(ctx: CanvasRenderingContext2D, W: number, H: number, look: LookPalette) {
  // Two soft discs of the accent — atmosphere when there's no photo.
  const g1 = ctx.createRadialGradient(W * 0.85, H * 0.15, 0, W * 0.85, H * 0.15, W * 0.7)
  g1.addColorStop(0, hexA(look.accent, look.dark ? 0.35 : 0.25))
  g1.addColorStop(1, hexA(look.accent, 0))
  ctx.fillStyle = g1
  ctx.fillRect(0, 0, W, H)
  const g2 = ctx.createRadialGradient(W * 0.1, H * 0.9, 0, W * 0.1, H * 0.9, W * 0.6)
  g2.addColorStop(0, hexA(look.ink, look.dark ? 0.08 : 0.06))
  g2.addColorStop(1, hexA(look.ink, 0))
  ctx.fillStyle = g2
  ctx.fillRect(0, 0, W, H)
}

async function paintPoster(ctx: CanvasRenderingContext2D, input: KitRenderInput, W: number, H: number, look: LookPalette) {
  const pad = Math.round(W * 0.07)
  ctx.fillStyle = look.bg
  ctx.fillRect(0, 0, W, H)
  const photo = input.photoDataUrl ? await loadImage(input.photoDataUrl).catch(() => null) : null
  const panelH = Math.round(H * 0.46)
  if (photo) {
    drawCover(ctx, photo, 0, 0, W, panelH)
  } else {
    ctx.fillStyle = look.accent
    ctx.fillRect(0, 0, W, panelH)
    paintTexture(ctx, W, panelH, { ...look, bg: look.accent })
  }
  const top = ctx.createLinearGradient(0, 0, 0, H * 0.12)
  top.addColorStop(0, 'rgba(0,0,0,0.35)')
  top.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = top
  ctx.fillRect(0, 0, W, H * 0.12)
  await drawBrand(ctx, input, pad, pad, Math.round(W * 0.07), '#ffffff')

  const maxW = W - pad * 2
  const qrSize = Math.round(W * 0.22)
  // Below the headline the QR owns the right-hand column.
  const narrowW = input.qrUrl ? maxW - qrSize - 56 : maxW
  let y = panelH + pad + 90
  const head = fitText(ctx, input.headline, (p) => `${p}px ${SERIF}`, maxW, 2, 132, 72)
  ctx.fillStyle = look.ink
  ctx.font = `${head.px}px ${SERIF}`
  y = drawLines(ctx, head.lines, pad, y, head.px * 0.98)
  if (input.subhead) {
    const sub = fitText(ctx, input.subhead, (p) => `400 ${p}px ${SANS}`, narrowW, 4, 40, 26)
    ctx.fillStyle = look.muted
    ctx.font = `400 ${sub.px}px ${SANS}`
    y = drawLines(ctx, sub.lines, pad, y + 26 + sub.px * 0.3, sub.px * 1.3)
  }
  if (input.offerLine) y = drawPill(ctx, input.offerLine, pad, y + 26, narrowW, 40, look)

  // QR bottom-right, dates bottom-left.
  const qrX = W - pad - qrSize
  const qrY = H - pad - qrSize - 44
  if (input.qrUrl) {
    try {
      const qrData = await renderBrandedQrDataUrl({
        data: input.qrUrl,
        size: qrSize * 2,
        style: posterQrStyle(look.dark ? '#111111' : look.ink, '#ffffff', look.accent),
        logoDataUrl: input.logoDataUrl,
        logoEnabled: Boolean(input.logoDataUrl),
      })
      const qr = await loadImage(qrData)
      ctx.fillStyle = '#ffffff'
      roundRect(ctx, qrX - 16, qrY - 16, qrSize + 32, qrSize + 32, 24)
      ctx.fill()
      ctx.drawImage(qr, qrX, qrY, qrSize, qrSize)
      ctx.fillStyle = look.muted
      ctx.font = `500 26px ${SANS}`
      ctx.textAlign = 'center'
      ctx.fillText(input.qrCaption || 'Scan for the menu', qrX + qrSize / 2, qrY + qrSize + 52)
      ctx.textAlign = 'left'
    } catch {
      /* a poster without a QR is still a poster */
    }
  }
  if (input.when) {
    ctx.fillStyle = look.ink
    ctx.font = `600 34px ${SANS}`
    ctx.fillText(input.when, pad, H - pad - 40)
  }
}

async function paintQuote(ctx: CanvasRenderingContext2D, input: KitRenderInput, W: number, H: number, look: LookPalette) {
  const pad = Math.round(W * 0.09)
  ctx.fillStyle = look.bg
  ctx.fillRect(0, 0, W, H)
  paintTexture(ctx, W, H, look)
  const photo = input.photoDataUrl ? await loadImage(input.photoDataUrl).catch(() => null) : null

  ctx.fillStyle = look.accent
  ctx.font = `${Math.round(W * 0.32)}px ${SERIF}`
  ctx.fillText('“', pad - 12, pad + W * 0.22)

  ctx.fillStyle = look.accent
  ctx.font = `${Math.round(W * 0.05)}px ${SANS}`
  ctx.fillText('★★★★★', pad, pad + W * 0.3)

  const text = input.quote?.text ?? ''
  const maxW = W - pad * 2
  const fitted = fitText(ctx, text, (p) => `${p}px ${SERIF}`, maxW, 8, 84, 44)
  ctx.fillStyle = look.ink
  ctx.font = `${fitted.px}px ${SERIF}`
  let y = drawLines(ctx, fitted.lines, pad, pad + W * 0.42, fitted.px * 1.12)
  ctx.fillStyle = look.muted
  ctx.font = `500 36px ${SANS}`
  ctx.fillText(`— ${input.quote?.author ?? 'A guest'}`, pad, y + 40)
  y += 40

  if (photo) {
    const r = Math.round(W * 0.11)
    const cx = W - pad - r
    const cy = H - pad - r
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.clip()
    drawCover(ctx, photo, cx - r, cy - r, r * 2, r * 2)
    ctx.restore()
  }
  await drawBrand(ctx, input, pad, H - pad - Math.round(W * 0.075), Math.round(W * 0.075), look.ink)
}

function hexA(hex: string, a: number): string {
  const m = hex.replace('#', '')
  const n = parseInt(m.length === 3 ? m.split('').map((c) => c + c).join('') : m, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/** Paint one visual. Waits for webfonts so the serif never falls back. */
export async function renderKitCanvas(input: KitRenderInput, canvas?: HTMLCanvasElement): Promise<HTMLCanvasElement> {
  const { w, h } = KIT_DIMS[input.format]
  const c = canvas ?? document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('Canvas not available')
  if (document.fonts?.ready) {
    try {
      await document.fonts.load(`64px ${SERIF}`)
      await document.fonts.ready
    } catch {
      /* non-fatal */
    }
  }
  const look = KIT_LOOKS[input.look]
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  if (input.format === 'poster') await paintPoster(ctx, input, w, h, look)
  else if (input.format === 'quote') await paintQuote(ctx, input, w, h, look)
  else await paintPhotoLayout(ctx, input, w, h, look)
  return c
}

export function canvasToBlob(c: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not export the image'))), type, 0.92),
  )
}

